import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { FilterConfig, FiltersConfig, FilterTypes } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import * as CropAidSelectors from 'src/app/crop-aid/store/crop-aid.selectors';
import { openFieldsDialog } from 'src/app/fields/store/actions';
import { ManureSpace, ManureTabType } from 'src/app/manure-space/models/manure-space.models';
import * as ManureSpaceActions from 'src/app/manure-space/store/manure-space.actions';

import { setFertilizersFromDelivery } from 'src/app/manure-space/store/manure-space.actions';
import * as ManureSpaceSelectors from 'src/app/manure-space/store/manure-space.selectors';
import { SalesOrderPeriod } from 'src/app/sales-order-period/models/sales-order-period.models';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { SiteState } from 'src/app/shared/models';
import { SoilType } from 'src/app/soil-type/models/soil-type.models';
import { selectSoilTypes } from 'src/app/soil-type/store/soil-type.selectors';
import * as AppActionTypes from 'src/app/store/actions';
import * as AppSelectors from 'src/app/store/selectors';
import { selectCustomerProgress } from 'src/app/store/selectors';

import { FertilizerImportDialogComponent } from '../fertilizer-import-dialog/fertilizer-import-dialog.component';

import { Fertilizer, FertilizerSource, FertilizerType } from '../models/fertilizer.model';
import * as FertilizersActions from '../store/actions';

export enum ButtonAction {
  EditFields = 'editFields',
  ImportVzc = 'importVzc',
  AnimalManureTransport = 'animalManureTransport',
  ImportMineralBalance = 'importMineralBalance',
  ImportAdvisedFertilizers = 'importAdvisedFertilizers',
}

const DefaultPeriodNumber = 3;

@Component({
  selector: 'app-fertilizers-overview',
  templateUrl: './fertilizers-overview.component.html',
  styleUrls: ['./fertilizers-overview.component.scss'],
  standalone: false,
})
export class FertilizersOverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  private salesOrderPeriods: SalesOrderPeriod[];
  private cropAids: CropAid[];
  private soilTypes: SoilType[];

  private readonly destroyed$ = new Subject<boolean>();
  private readonly cropYearChanged$ = new Subject<number>();
  private readonly cropAids$ = this.store.select(CropAidSelectors.selectCropAidFertilizers);
  private readonly soilTypes$ = this.store.pipe(
    select(selectSoilTypes),
    takeUntil(this.destroyed$),
  );
  private readonly salesOrderPeriods$ = this.store.pipe(
    select(SalesOrderPeriodSelectors.selectSalesOrderPeriods),
  );
  private defaultSalesOrderPeriod: SalesOrderPeriod;
  public readonly cropYear$ = this.store.select(AppSelectors.getCropYear);
  public readonly availableCropYears$ = this.store.select(AppSelectors.getAvailableCropYears);
  public readonly defaultCropYear$ = this.store.select(AppSelectors.getDefaultCropYear);
  public readonly customer$ = this.store.select(UserSelectors.selectCustomer);

  public readonly filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      year: {
        label: 'year',
        type: FilterTypes.Select,
        options: this.availableCropYears$.pipe(
          map((availableYears) =>
            availableYears.map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          ),
        ),
        value: this.cropYear$,
        defaultValue: this.defaultCropYear$,
        changes: this.cropYearChanged$,
      },
    },
  };

  public readonly buttonItems = [
    {
      viewValue: 'fields.title',
      key: ButtonAction.EditFields,
      icon: 'map',
    },
    {
      viewValue: 'sewageSludgeAndCompost.title',
      key: ButtonAction.ImportVzc,
      icon: 'local_shipping',
    },
    {
      viewValue: 'animalManureTransport.datepickerDialogLabel',
      key: ButtonAction.AnimalManureTransport,
      icon: 'local_shipping',
    },
    {
      viewValue: 'importMineralBalance',
      key: ButtonAction.ImportMineralBalance,
      icon: 'eco',
    },
    {
      viewValue: 'importAdvisedFertilizers',
      key: ButtonAction.ImportAdvisedFertilizers,
      icon: 'eco',
    },
  ];

  public readonly customerProgress$ = this.store.select(selectCustomerProgress);
  public readonly hasSecondaryAction$ = this.customerProgress$.pipe(
    map((customerProgress) => customerProgress !== undefined),
  );

  secondaryActionIcon$ = this.customerProgress$.pipe(
    map((customerProgress) => {
      if (!customerProgress) {
        return '';
      }

      if (customerProgress.processes.manurespaceCalculationsCompleted) {
        return 'check_box';
      } else {
        return 'check_box_outline_blank';
      }
    }),
  );
  manureDetailsData: ManureSpace | null = null;
  manureSpaceByManureCodes: ManureSpace | null = null;
  manureSpaceByPeriodData: ManureSpace | null = null;
  currentTabSubcription: Subscription | null;

  ngOnInit() {
    combineLatest([this.salesOrderPeriods$, this.cropAids$, this.soilTypes$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([sop, cropAids, soilTypes]) => {
          this.salesOrderPeriods = sop;
          this.cropAids = cropAids;
          this.soilTypes = soilTypes;
        },
      });

    this.watchCropYear();

    this.salesOrderPeriods$.pipe(takeUntil(this.destroyed$)).subscribe((salesOrderPeriods) => {
      this.defaultSalesOrderPeriod = salesOrderPeriods.find(
        (s) => s.periodNumber === DefaultPeriodNumber,
      );
    });
  }

  public readonly manureTypeResource$ = this.store.pipe(
    select(ManureSpaceSelectors.selectManureSpaceFilteredByManureCodeResource),
  );

  public readonly manurePeriodResource$ = this.store.pipe(
    select(ManureSpaceSelectors.selectManureSpaceByPeriodResource),
  );

  public readonly manureSpaceResource$ = this.store.pipe(
    select(ManureSpaceSelectors.selectManureSpaceDetailsResource),
  );

  activeTabIndex = signal<ManureTabType>(ManureTabType.ManureType);
  readonly TabType = ManureTabType;

  currentTabLoading = computed(() => {
    const tabIndex = this.activeTabIndex();
    switch (tabIndex) {
      case ManureTabType.ManureType: {
        const state = this.store.selectSignal(
          ManureSpaceSelectors.selectManureSpaceFilteredByManureCodeResource,
        )()?.state;
        return state === 'loading' || state === 'uninitialized';
      }
      case ManureTabType.ManurePeriod: {
        const state = this.store.selectSignal(
          ManureSpaceSelectors.selectManureSpaceByPeriodResource,
        )()?.state;
        return state === 'loading' || state === 'uninitialized';
      }
      case ManureTabType.ManureDetails: {
        const state = this.store.selectSignal(
          ManureSpaceSelectors.selectManureSpaceDetailsResource,
        )()?.state;
        return state === 'loading' || state === 'uninitialized';
      }
      default:
        return true;
    }
  });

  onTabChange(event: MatTabChangeEvent): void {
    const tabIndex = event.index as ManureTabType;
    this.activeTabIndex.set(tabIndex);
    this.store.dispatch(ManureSpaceActions.setActiveTab({ tabIndex }));
    if (this.currentTabSubcription) {
      this.currentTabSubcription.unsubscribe();
    }
    this.loadDataForActiveTab();
  }

  activeTabIndexValue(): number {
    return this.activeTabIndex();
  }

  loadDataForActiveTab(): void {
    const tabIndex = this.activeTabIndex();
    switch (tabIndex) {
      case 0:
        this.loadManureTypeData();
        break;
      case 1:
        this.loadManurePeriodData();
        break;
      case 2:
        this.loadManureDetailsData();
        break;
    }
  }

  loadManureTypeData(): void {
    this.currentTabSubcription = this.manureTypeResource$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((resource) => {
        if (resource?.state !== 'loaded' && resource?.state !== 'loading') {
          this.store.dispatch(ManureSpaceActions.getManureSpaceByManureCode());
          return;
        }
        if (resource.state === 'loaded') {
          this.manureSpaceByManureCodes = resource?.data;
        }
      });
  }

  loadManurePeriodData(): void {
    this.currentTabSubcription = this.manurePeriodResource$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((resource) => {
        if (resource?.state !== 'loaded' && resource?.state !== 'loading') {
          this.store.dispatch(ManureSpaceActions.getManureSpaceByPeriod());
          return;
        }

        if (resource?.state === 'loaded') {
          const manureSpace = resource.data;
          this.manureSpaceByPeriodData = manureSpace;
        }
      });
  }

  watchCropYear() {
    this.cropYearChanged$
      .pipe(withLatestFrom(this.cropYear$), takeUntil(this.destroyed$))
      .subscribe(([newYear, existingYear]) => {
        if (newYear && newYear !== existingYear) {
          this.store.dispatch(AppActionTypes.SetCropYear({ cropYear: newYear }));
        }
        if (newYear) {
          this.loadDataForActiveTab();
        }
      });
  }

  loadManureDetailsData(): void {
    this.currentTabSubcription = this.manureSpaceResource$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((resource) => {
        if (resource?.state !== 'loaded' && resource?.state !== 'loading') {
          this.store.dispatch(ManureSpaceActions.getManureSpace());
          return;
        }
        if (resource.state === 'loaded') {
          const manureSpace = resource?.data;
          this.manureDetailsData = manureSpace;
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buttonAction(data: string) {
    switch (data) {
      case ButtonAction.EditFields:
        this.store.dispatch(openFieldsDialog());
        break;
      case ButtonAction.ImportVzc:
        this.store.dispatch(FertilizersActions.fetchVzcTransports());
        break;
      case ButtonAction.AnimalManureTransport:
        this.store.dispatch(FertilizersActions.fetchAnimalManureTransports());
        break;
      case ButtonAction.ImportMineralBalance:
        this.store.dispatch(FertilizersActions.startFertilizerImportDialogFlow());
        break;
      case ButtonAction.ImportAdvisedFertilizers:
        this.cropYear$.pipe(take(1)).subscribe((cropYear) => {
          if (cropYear) {
            this.store.dispatch(ManureSpaceActions.getAdvisedFertilizers({ cropYear }));
          }
        });
        break;
    }
  }

  addFertilizer() {
    this.dialog
      .actions({
        options: [
          {
            value: FertilizerType.InitialStock,
            name: this.locale.translate('initialStock'),
            icon: 'archive',
          },
          {
            value: FertilizerType.NotDelivered,
            name: this.locale.translate('isNotDelivered'),
            icon: 'local_shipping',
          },
          {
            value: FertilizerType.Delivered,
            name: this.locale.translate('isDelivered'),
            icon: 'check',
          },
          {
            value: FertilizerType.Dispatched,
            name: this.locale.translate('dispatched'),
            imgPath: '../../../assets/img/local_shipping_reverse.png',
          },
          {
            value: FertilizerType.ClosingStock,
            name: this.locale.translate('closingStock'),
            icon: 'unarchive',
          },
        ],
      })
      .subscribe(({ result, status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          const fertilizer: Fertilizer = {
            id: null as string,
            cropAidId: null as number,
            dosage: null as number,
            totalNitrogen: null as number,
            totalPhosphate: null as number,
            soilTypeId: null as number,
            comment: null as string,
            periodId: this.defaultSalesOrderPeriod?.id ?? (null as number),
            fertilizerType: result,
            fertilizerSource: FertilizerSource.Manual,
            manureCode: null as number,
          };

          this.upsertFertilizer(fertilizer);
        }
      });
  }

  upsertFertilizer(fertilizer: Fertilizer) {
    this.store.dispatch(
      setFertilizersFromDelivery({
        fertilizers: [fertilizer],
      }),
    );

    this.dialog
      .dialog(FertilizerImportDialogComponent, {
        title: this.locale.translate('edit'),
        confirmButton: true,
        fertilizerSource: fertilizer.fertilizerSource,
        isOverviewEditFertilizerId: fertilizer.id,
        isBatchEdit: false,
      })
      .pipe(
        switchMap(() => of(AppActionTypes.CloseImportDialog())),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  openFieldPlan() {
    this.store.dispatch(AppActionTypes.NavigateToFieldPlan());
  }

  secondaryAction() {
    this.store.dispatch(AppActionTypes.ToggleCustomerProgressManure());
  }

  getPeriodName = (periodId: number): string =>
    this.salesOrderPeriods.find((p) => p.id === periodId)?.name;

  getSoilTypeName = (soilTypeId: number): string =>
    this.soilTypes.find((s) => s.id === soilTypeId)?.name;

  getCropAidName = (cropAidId: number): string =>
    this.cropAids.find((s) => s.id === cropAidId)?.name;

  hasNoData() {
    return !(
      this.manureDetailsData ||
      this.manureSpaceByPeriodData ||
      this.manureSpaceByManureCodes
    );
  }
}
